import { useTranslation } from 'react-i18next';

import { Button } from '@codes/ui-kit/components/ui/button';
import { AgentView } from '@lemoncloud/ssocio-chatbots-api';
import { ChevronLeft, ChevronRight, Loader2, Plus } from 'lucide-react';

import { AgentListItem } from './AgentListItem';

interface MyAgentsListProps {
    agents: AgentView[];
    isLoading: boolean;
    selectedAgentId: string | null;
    searchQuery: string;
    onAgentSelect: (agentId: string) => void;
    onCreateNew: () => void;
    onSearchChange: (query: string) => void;
    onChatHistory?: (agentId: string) => void;
    currentPage: number;
    totalCount: number;
    pageSize: number;
    paginationRange: (number | string)[];
    onPageChange: (page: number) => void;
}

export const MyAgentsList = ({
    agents,
    isLoading,
    selectedAgentId,
    searchQuery,
    onAgentSelect,
    onCreateNew,
    onSearchChange,
    onChatHistory,
    currentPage,
    totalCount,
    pageSize,
    paginationRange,
    onPageChange,
}: MyAgentsListProps) => {
    const { t } = useTranslation();
    const totalPages = Math.ceil(totalCount / pageSize);

    return (
        <div className="h-[calc(100vh-184px)] top-[38px] md:sticky bg-white dark:bg-[#1C1C1D] shadow-[0_0_10px_rgba(0,0,0,0.06)] border border-chatbot-line rounded-[16px] overflow-hidden flex flex-col max-md:max-h-[50vh] max-md:overflow-auto">
            {/* TODO: add search */}
            {/*<div className="mb-4 p-4 pb-0">*/}
            {/*    <div className="h-[29px] bg-white dark:bg-[#1C1C1D] shadow-[0_0_10px_rgba(0,0,0,0.06)] border border-chatbot-line flex items-center justify-between gap-[10px] rounded-md py-[6px] pr-2 pl-[7px] w-[178px]">*/}
            {/*        <input*/}
            {/*            placeholder={t('agent.list.search.placeholder')}*/}
            {/*            value={searchQuery}*/}
            {/*            onChange={e => onSearchChange(e.target.value)}*/}
            {/*            className="flex-1 w-full bg-white dark:bg-[#1C1C1D] outline-0 text-xs"*/}
            {/*        />*/}
            {/*        <Search className="w-4 h-4 text-text-400" />*/}
            {/*    </div>*/}
            {/*</div>*/}

            {/* 헤더 */}
            <div className="flex items-center justify-between py-[14px] px-4">
                <div className="font-medium text-sm">{t('agent.list.header')}</div>
                <Button
                    variant="outline"
                    className="gap-1 rounded-md pl-[6px] pr-[9px] text-[13px] h-8 text-text"
                    onClick={onCreateNew}
                >
                    <Plus className="w-[18px] h-[18px]" /> {t('agent.list.actions.addAgent')}
                </Button>
            </div>

            {/* 목록 */}
            <div className="px-4 flex-1 mt-[2px] overflow-auto">
                {isLoading ? (
                    <div className="flex items-center justify-center h-full">
                        <Loader2 className="h-5 w-5 animate-spin" />
                    </div>
                ) : agents.length > 0 ? (
                    <ul className="space-y-2">
                        {agents.map(agent => (
                            <AgentListItem
                                key={agent.id}
                                agent={agent}
                                isSelected={agent.id === selectedAgentId}
                                onSelect={() => agent.id && onAgentSelect(agent.id)}
                                onChatHistory={onChatHistory}
                            />
                        ))}
                    </ul>
                ) : (
                    <div className="text-text-600 flex items-center justify-center h-full">
                        {t('agent.list.states.empty')}
                    </div>
                )}
            </div>
            {totalCount > 0 && (
                <div className="flex items-center justify-center space-x-2 p-4">
                    <button
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 0}
                        className="h-[26px] w-[26px] rounded-full border border-gray-300 flex items-center justify-center disabled:opacity-30"
                    >
                        <ChevronLeft className="w-[18px] h-[18px]" />
                    </button>

                    {paginationRange.map((pageNumber, idx) => (
                        <button
                            key={idx}
                            onClick={() => typeof pageNumber === 'number' && onPageChange(pageNumber)}
                            disabled={typeof pageNumber !== 'number'}
                            className={`h-8 flex items-center justify-center text-[15px] font-medium ${
                                pageNumber === currentPage ? 'text-point' : 'text-gray-700'
                            }`}
                        >
                            {typeof pageNumber === 'number' ? pageNumber + 1 : '...'}
                        </button>
                    ))}

                    <button
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage >= totalPages - 1}
                        className="h-[26px] w-[26px] rounded-full border border-gray-300 flex items-center justify-center disabled:opacity-30"
                    >
                        <ChevronRight className="w-[18px] h-[18px]" />
                    </button>
                </div>
            )}
        </div>
    );
};
